import { GC_TIME, QUERY_PRESETS, STALE_TIME } from "@/queries/queryTimeConfig";

export const subscriptionConfig = {
  getSubscriptions: {
    key: "getSubscriptions",
    url: "/api/v1/subscriptions",
    method: "GET",
    options: {
      ...QUERY_PRESETS.LIST,
    },
  },

  getSubscriptionById: {
    key: "getSubscriptionById",
    url: (params) => `/api/v1/subscriptions/${params?.id || params}`,
    method: "GET",
    options: {
      ...QUERY_PRESETS.DETAIL,
    },
  },

  getSubscriptionByRegistrationId: {
    key: "getSubscriptionByRegistrationId",
    url: (params) =>
      `/api/v1/subscriptions/by-registration/${params?.registrationId || params}`,
    method: "GET",
    options: {
      ...QUERY_PRESETS.DETAIL,
    },
  },

  getMySubscriptionHistory: {
    key: "getMySubscriptionHistory",
    url: "/api/v1/subscriptions/me/history",
    method: "GET",
    options: {
      ...QUERY_PRESETS.LIST,
    },
  },

  getMyPendingRenewal: {
    key: "getMyPendingRenewal",
    url: "/api/v1/subscriptions/me/pending-renewal",
    method: "GET",
    options: {
      ...QUERY_PRESETS.DETAIL,
    },
  },

  getRenewalProrationPreview: {
    key: "getRenewalProrationPreview",
    url: "/api/v1/subscriptions/me/renewal-proration",
    method: "GET",
    options: {
      // Server proration is calendar-day stable; REALTIME + refetchOnWindowFocus caused
      // duplicate network calls. Submit still refetches immediately before createRenewalRequest.
      staleTime: STALE_TIME.LONG,
      gcTime: GC_TIME.MEDIUM,
      refetchOnWindowFocus: false,
    },
  },

  getSubscriptionProrationPreview: {
    key: "getSubscriptionProrationPreview",
    url: (data) =>
      `/api/v1/subscriptions/${data?.id || data}/proration-preview`,
    method: "GET",
    options: {
      ...QUERY_PRESETS.DETAIL,
      refetchOnWindowFocus: false,
    },
  },

  createRenewalRequest: {
    key: "createRenewalRequest",
    url: "/api/v1/subscriptions/renewal-request",
    method: "POST",
    options: {
      retry: 0,
      invalidateKeys: [
        "getProfileDetails",
        "getMySubscriptionHistory",
        "getMyPendingRenewal",
        "getSubscriptions",
        "getRenewalProrationPreview",
      ],
      onSuccessMessage: "Renewal request submitted successfully",
      onErrorMessage: "Failed to submit renewal request",
    },
  },

  cancelRenewalRequest: {
    key: "cancelRenewalRequest",
    url: (data) =>
      `/api/v1/subscriptions/${data?.id || data}/cancel-pending`,
    method: "PATCH",
    options: {
      retry: 0,
      invalidateKeys: [
        "getMyPendingRenewal",
        "getMySubscriptionHistory",
        "getSubscriptions",
      ],
      onSuccessMessage: "Renewal request cancelled",
      onErrorMessage: "Failed to cancel renewal request",
    },
  },

  approveRenewalRequest: {
    key: "approveRenewalRequest",
    url: (data) => `/api/v1/subscriptions/${data?.id || data}/approve`,
    method: "PATCH",
    options: {
      retry: 0,
      invalidateKeys: [
        "getSubscriptions",
        "getSubscriptionByRegistrationId",
        "getSubscriptionHistoryByRegistrationId",
        "getMySubscriptionHistory",
        "getMyPendingRenewal",
        "getProfileDetails",
      ],
      onSuccessMessage: "Renewal request approved",
      onErrorMessage: "Failed to approve renewal request",
    },
  },

  // ==================== MID-PERIOD UPGRADE ====================

  getUpgradeProrationPreview: {
    key: "getUpgradeProrationPreview",
    url: (data) =>
      `/api/v1/subscriptions/${data?.id || data}/upgrade-proration-preview`,
    method: "GET",
    options: {
      ...QUERY_PRESETS.DETAIL,
      refetchOnWindowFocus: false,
    },
  },

  previewSubscriptionUpgrade: {
    key: "previewSubscriptionUpgrade",
    url: "/api/v1/subscriptions/upgrade-preview",
    method: "POST",
    options: {
      retry: 0,
      onErrorMessage: "Failed to load upgrade preview",
    },
  },

  createSubscriptionUpgradeRequest: {
    key: "createSubscriptionUpgradeRequest",
    url: "/api/v1/subscriptions/upgrade-request",
    method: "POST",
    options: {
      retry: 0,
      invalidateKeys: [
        "getProfileDetails",
        "getMySubscriptionHistory",
        "getMyPendingRenewal",
        "getSubscriptions",
      ],
      onSuccessMessage: "Upgrade request submitted successfully",
      onErrorMessage: "Failed to submit upgrade request",
    },
  },

  approveSubscriptionUpgrade: {
    key: "approveSubscriptionUpgrade",
    url: (data) =>
      `/api/v1/subscriptions/${data?.id || data}/approve-upgrade`,
    method: "PATCH",
    options: {
      retry: 0,
      invalidateKeys: [
        "getSubscriptions",
        "getSubscriptionByRegistrationId",
        "getSubscriptionHistoryByRegistrationId",
        "getMySubscriptionHistory",
        "getMyPendingRenewal",
        "getProfileDetails",
      ],
      onSuccessMessage: "Mid-period upgrade approved",
      onErrorMessage: "Failed to approve upgrade",
    },
  },

  rejectRenewalRequest: {
    key: "rejectRenewalRequest",
    url: (data) => `/api/v1/subscriptions/${data?.id || data}/reject`,
    method: "PATCH",
    options: {
      retry: 0,
      invalidateKeys: [
        "getSubscriptions",
        "getMySubscriptionHistory",
        "getMyPendingRenewal",
      ],
      onSuccessMessage: "Renewal request rejected",
      onErrorMessage: "Failed to reject renewal request",
    },
  },

  getSubscriptionHistoryByRegistrationId: {
    key: "getSubscriptionHistoryByRegistrationId",
    url: (params) =>
      `/api/v1/subscriptions/by-registration/${params?.registrationId || params}/history`,
    method: "GET",
    options: {
      ...QUERY_PRESETS.LIST,
    },
  },

  createSubscription: {
    key: "createSubscription",
    url: "/api/v1/subscriptions",
    method: "POST",
    options: {
      retry: 2,
      invalidateKeys: [
        "getSubscriptions",
        "getSubscriptionByRegistrationId",
        "getPropertyOwnerRegistrations",
      ],
      onSuccessMessage: "Subscription created successfully",
      onErrorMessage: "Failed to create subscription",
    },
  },

  selectSubscriptionPlanByToken: {
    key: "selectSubscriptionPlanByToken",
    url: "/api/v1/subscriptions/select-by-token",
    method: "POST",
    options: {
      retry: 0,
      invalidateKeys: ["verifyToken"],
      onErrorMessage: "Failed to select subscription plan",
    },
  },

  updateSubscription: {
    key: "updateSubscription",
    url: (data) => `/api/v1/subscriptions/${data?.id || data}`,
    method: "PATCH",
    options: {
      retry: 2,
      invalidateKeys: [
        "getSubscriptions",
        "getSubscriptionById",
        "getSubscriptionByRegistrationId",
        "getPropertyOwnerRegistrations",
      ],
      onSuccessMessage: "Subscription updated successfully",
      onErrorMessage: "Failed to update subscription",
    },
  },

  activateSubscription: {
    key: "activateSubscription",
    url: (data) => `/api/v1/subscriptions/${data?.id || data}`,
    method: "PATCH",
    options: {
      retry: 2,
      invalidateKeys: [
        "getSubscriptions",
        "getSubscriptionById",
        "getSubscriptionByRegistrationId",
        "getPropertyOwnerRegistrations",
      ],
      onSuccessMessage: "Subscription activated successfully",
      onErrorMessage: "Failed to activate subscription",
    },
  },

  expireSubscription: {
    key: "expireSubscription",
    url: (data) => `/api/v1/subscriptions/${data?.id || data}`,
    method: "PATCH",
    options: {
      retry: 2,
      invalidateKeys: [
        "getSubscriptions",
        "getSubscriptionById",
        "getSubscriptionByRegistrationId",
        "getPropertyOwnerRegistrations",
      ],
      onSuccessMessage: "Subscription expired successfully",
      onErrorMessage: "Failed to expire subscription",
    },
  },

  deactivateSubscription: {
    key: "deactivateSubscription",
    url: (data) => `/api/v1/subscriptions/${data?.id || data}`,
    method: "PATCH",
    options: {
      retry: 2,
      invalidateKeys: [
        "getSubscriptions",
        "getSubscriptionById",
        "getPropertyOwnerRegistrations",
      ],
      onSuccessMessage: "Subscription deactivated successfully",
      onErrorMessage: "Failed to deactivate subscription",
    },
  },

  deleteSubscription: {
    key: "deleteSubscription",
    url: (data) => `/api/v1/subscriptions/${data?.id || data}`,
    method: "DELETE",
    options: {
      retry: 2,
      invalidateKeys: [
        "getSubscriptions",
        "getSubscriptionByRegistrationId",
        "getPropertyOwnerRegistrations",
      ],
      onSuccessMessage: "Subscription deleted successfully",
      onErrorMessage: "Failed to delete subscription",
    },
  },
};
